import { createHandlerChainRunner, HandlerWithResult } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { WorkBook } from "xlsx";

// Verndale Customization - HWSXOP-2654 - Add bagQty to parsedItems
export interface VmiCountsImportItem {
    name: string;
    binNumber: string;
    minQty?: string | null;
    maxQty?: string | null;
    count: string | null;          // LastCount QTY
    unitOfMeasure?: string | null; // U/M
    bagQty?: string | null;        // Bag QTY
}
// End Verndale Customization - HWSXOP-2654

export interface ProcessFileParameter {
    data: ArrayBuffer;
    fileExtension: string;
    firstRowHeading: boolean;
}

export interface ProcessFileResult {
    // Verndale Customization - HWSXOP-2654 - Add bagQty to parsedItems
    parsedItems: VmiCountsImportItem[];
    uploadLimitExceeded: boolean;
    isBadFile: boolean;
    isUploading: boolean;
}

type HandlerType = HandlerWithResult<ProcessFileParameter, ProcessFileResult>;

export const DispatchBeginProcessFile: HandlerType = props => {
    props.dispatch({
        type: "Components/VmiCountsImportModal/BeginProcessFile",
    });
};

export const CreateDefaultResult: HandlerType = props => {
    props.result = {
        parsedItems: [],
        uploadLimitExceeded: false,
        isBadFile: false,
        isUploading: false,
    };
};

export const ProcessUploadedFile: HandlerType = async props => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

    const data = fixData(props.parameter.data);
    try {
        if (props.parameter.fileExtension === "xls" || props.parameter.fileExtension === "xlsx") {
            const workbook = XLSX.read(btoa(data), { type: "base64" });
            if (workbook) {
                await processWorkBook(workbook, props.parameter.firstRowHeading, props.result);
            }
        } else if (props.parameter.fileExtension === "csv") {
            await processCsv(data, props.parameter.firstRowHeading, props.result);
        } else {
            props.result.isBadFile = true;
        }
    } catch (error) {
        logger.error(error);
        props.result.isBadFile = true;
    }

    if (!props.result.isBadFile && !props.result.uploadLimitExceeded) {
        props.result.isUploading = true;
    }
};

const fixData = (data: ArrayBuffer) => {
    let o = "";
    let l = 0;
    const w = 10240;
    for (; l < data.byteLength / w; ++l) {
        o += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w, l * w + w)) as any);
    }

    o += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w)) as any);

    return o;
};

const processWorkBook = async (workBook: WorkBook, firstRowHeading: boolean, result: ProcessFileResult) => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

    result.parsedItems = [];
    workBook.SheetNames.forEach(sheetName => {
        const opts = { header: 1 };
        let rows = XLSX.utils.sheet_to_json<string>(workBook.Sheets[sheetName], opts);
        if (rows.length > 0) {
            if (firstRowHeading) {
                rows = rows.slice(1, rows.length);
            }

            rows = rows.filter(r => r[0] != null && r[0].length > 0);

            if (limitExceeded(rows.length, firstRowHeading, result)) {
                return;
            }

            rows.forEach(s => {
                // Verndale Customization - HWSXOP-2654 - Add bagQty to parsedItems
                const objectToAdd: VmiCountsImportItem = {
                    name: s[0] ?? "",
                    binNumber: s[1] ?? "",
                    minQty: s[2] ?? null,
                    maxQty: s[3] ?? null,
                    count: s[4] ?? null,          // LastCount QTY
                    unitOfMeasure: s[5] ?? null,  // U/M
                    bagQty: s[6] ?? null,         // Bag QTY
                };

                result.parsedItems.push(objectToAdd);
            });
        }
    });
};

const processCsv = async (data: string, firstRowHeading: boolean, result: ProcessFileResult) => {
    const { parse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    result.parsedItems = [];
    const newLineIndex = data.lastIndexOf("\r\n");
    if (newLineIndex + 2 === data.length) {
        data = data.substr(0, newLineIndex);
    }

    const results = parse<(string | null)[]>(data, {
        skipEmptyLines: true,
    });
    if (results.errors.length > 0) {
        result.isBadFile = true;
        return;
    }

    let rows = results.data;
    if (rows.length > 0) {
        if (firstRowHeading) {
            rows = rows.slice(1, rows.length);
        }

        if (limitExceeded(rows.length, firstRowHeading, result)) {
            return;
        }

        rows.forEach((s: (string | null)[]) => {
            // Verndale Customization - HWSXOP-2654 - Add bagQty to parsedItems
            const objectToAdd: VmiCountsImportItem = {
                name: s[0] ?? "",
                binNumber: s[1] ?? "",
                minQty: s[2] ?? null,
                maxQty: s[3] ?? null,
                count: s[4] ?? null,          // LastCount QTY
                unitOfMeasure: s[5] ?? null,  // U/M
                bagQty: s[6] ?? null,         // Bag QTY
            };

            result.parsedItems.push(objectToAdd);
        });
    }
};

const limitExceeded = (rowsCount: number, skipFirstRow: boolean, result: ProcessFileResult) => {
    const maximumAllowedRowsCount = 500 + (skipFirstRow ? 1 : 0);
    result.uploadLimitExceeded = rowsCount > maximumAllowedRowsCount;
    return result.uploadLimitExceeded;
};

export const DispatchCompleteProcessFile: HandlerType = props => {
    props.dispatch({
        type: "Components/VmiCountsImportModal/CompleteProcessFile",
        result: props.result,
    });
};

export const chain = [DispatchBeginProcessFile, CreateDefaultResult, ProcessUploadedFile, DispatchCompleteProcessFile];

const processFile = createHandlerChainRunner(chain, "ProcessFile");
export default processFile;
